import {Col, Row} from 'react-bootstrap'
import AppSubmitButtonContainer from '../../../components/AppSubmitButtonContainer'
import AppButton from '../../../components/AppButton'
import AppListItems from '../../../components/AppListItems'
import {ChangeEvent, useEffect, useRef, useState} from 'react'
import {
  eventLogType,
  IincidentReportList,
  incidentListReportType,
  incidentReportcolumn,
  reportListTypeFromServer,
} from './model'
import Chart from 'react-google-charts'
import IncidentTableData from './IncidentTableData'
import {useConvertToPdf} from '../../../hooks/useConvertToPdf'
import {getIncidentReports, readGraphData, uploadIncidentReport} from './service'
import {extractDateFromReportListItem, formatIncidentReport, mapFields} from './formatList'
import {AppToast} from '../../../Utils/AppToast'
import {convertExcelDataToTableAndCharts} from './convertExcelDataToTableAndChart'
import AppLoaderWithContainer from '../../../components/AppLoaderWithContainer'
import autoTable from 'jspdf-autotable'
import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'
const acceptedExcelFileString = '.xlsx'
type IncidentReportProps = {
  isIncidentTab: boolean
}

const IncidentReport = ({isIncidentTab}: IncidentReportProps) => {
  const [exportToPdfLoading, setExportToPdfLoading] = useState(false)

  const {toast} = AppToast

  const fileInputRef = useRef<HTMLInputElement>(null)
  const reportRef = useRef<HTMLDivElement>(null)

  const [reportFetchLoading, setReportFetchLoading] = useState(false)
  const [reportUploadLoading, setReportUploadLoading] = useState(false)
  const [chartTableDataUploading, setChartTableDataUploading] = useState(false)

  const [tableMapData, setTableMapData] = useState(() => new Map<string, IincidentReportList>())
  const [chartMapData, setChartMapData] = useState(
    () => new Map<string, {event: string; count: number}>()
  )

  const tableData = Array.from(tableMapData.values())
  const chartData = Array.from(chartMapData?.values())?.map(({count, event}) => [event, count])

  const [incidentReports, setIncidentReports] = useState<incidentListReportType[]>(() => [])
  const [incidentReportsSelected, setIncidentReportsSelected] = useState(
    new Map<string, incidentListReportType>()
  )
  // Chart data for the Incident Report (Bar chart)
  const incidentChartData: (string | number)[][] = [['Event Type', 'Events'], ...chartData]

  const setGraphTableDataAfterReportsClicked = (data: eventLogType[]) => {
    const {chartMapData, tableMapData} = convertExcelDataToTableAndCharts(data)
    setChartMapData(chartMapData)
    setTableMapData(tableMapData)
  }

  const handleGetIncidentReportClick = async () => {
    try {
      setReportFetchLoading(true)
      setReportFetchLoading(true)
      const reportIds = Array.from(incidentReportsSelected.keys())?.map((id) => Number(id))
      const {data} = await readGraphData(reportIds)
      if (!data.success) {
        toast.error('Something went wrong, Could not read report')
        return
      }
      setGraphTableDataAfterReportsClicked(data.data)
    } catch (error) {
      console.error('read graph data error', error)
    } finally {
      setReportFetchLoading(false)
      setChartTableDataUploading(false)
    }
  }

  const getListReportStyle = (isSelected: boolean): React.CSSProperties => {
    return {
      cursor: 'pointer',
      backgroundColor: isSelected ? '#007bff' : '',
      color: isSelected ? 'white' : '',
    }
  }
  const getIsIncidentReportSelected = (report: incidentListReportType) => {
    const reportId = report['id']
    return !!incidentReportsSelected.has(reportId)
  }

  const getIncidentReportStyle = (report: incidentListReportType) => {
    return getListReportStyle(getIsIncidentReportSelected(report))
  }

  const updateTheSelectedIncidentReport = (
    report: incidentListReportType,
    setMap: React.Dispatch<React.SetStateAction<Map<string, incidentListReportType>>>
  ) => {
    const {id, date, fileName, label} = report
    setMap((prev) => {
      const newMap = new Map(prev)
      if (newMap.has(id)) {
        newMap.delete(id)
      } else {
        newMap.set(id, {date, fileName, id, label})
      }
      return newMap
    })
  }

  const handleIncidentReportClick = (report: incidentListReportType) => {
    updateTheSelectedIncidentReport(report, setIncidentReportsSelected)
  }

  const setTheUploadedFileAsSelectedReport = (report: reportListTypeFromServer) => {
    const {id, filename, upload_date} = report
    setIncidentReports((prev) => [{...mapFields(report, 1)}, ...prev])
    const newMap = new Map<string, incidentListReportType>()
    newMap.set(id, {date: upload_date, fileName: filename, id, label: ''})
    setIncidentReportsSelected(newMap)
  }

  const handleUploadFile = async (file: File) => {
    try {
      setReportUploadLoading(true)
      setChartTableDataUploading(true)
      const {data} = await uploadIncidentReport(file)
      if (!data.success) {
        const message = data?.message
        const defaultMsg = 'Something went wrong, Report Could not be uploaded'
        toast.error(message ?? defaultMsg)
        return
      }
      const reportData = data.data?.excelData?.data
      const uploadedReportData = data.data?.uploadedReport
      setTheUploadedFileAsSelectedReport(uploadedReportData)
      const {chartMapData, tableMapData} = convertExcelDataToTableAndCharts(reportData)
      setChartMapData(chartMapData)
      setTableMapData(tableMapData)
    } catch (error) {
      console.error('schedule report upload error', error)
      toast.error('File not uploaded, Something went wrong')
    } finally {
      setReportUploadLoading(false)
      setChartTableDataUploading(false)
    }
  }

  //file upload btn click, opens the file dialog
  const handleFileInputClick = () => {
    if (!fileInputRef?.current?.click()) return
    fileInputRef.current.click()
  }

  //handle file change is called by getFile internally
  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files) return null
    var file = event?.target?.files[0]
    event.target.value = ''
    return file
  }

  const getFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = handleFileChange(event)
    if (!file) return
    handleUploadFile(file)
  }

  const handleExcelUploadBtnClick = () => {
    if (fileInputRef.current) fileInputRef.current.accept = acceptedExcelFileString
    handleFileInputClick()
  }

  const fetchIncidentReports = async () => {
    try {
      setReportFetchLoading(true)
      const {data} = await getIncidentReports()
      if (data.success && data.data && data.data?.length > 0) {
        const reportData = data.data
        setIncidentReports(formatIncidentReport(reportData))
        toast.success('Incident Reports found')
      } else {
        toast.error('Sorry, no incident reports found')
        setIncidentReports([])
      }
    } catch (error) {
      console.error('fetch incident reports error', error)
      toast.error('Unable to get Incident Reports')
    } finally {
      setReportFetchLoading(false)
    }
  }

  const getHeadingInPdfExport = () => {
    const textArray = []
    textArray.push({
      text: 'Reports selected',
      yPosition: 10,
      fontSize: 13,
    })

    const arrayOfSelectedReports = Array.from(incidentReportsSelected.values())
    arrayOfSelectedReports.forEach((report, index) => {
      const {date, fileName} = report
      const number = index + 1
      const formattedDate = extractDateFromReportListItem(date)
      textArray.push({
        text: `${number}-${fileName} - Date - ${formattedDate}`,
        yPosition: 8,
        fontSize: 13,
      })
    })
    return textArray
  }

  const exportChartAndTableToPdf = async () => {
    try {
      if (!reportRef?.current) return
      setExportToPdfLoading(true)
      const pdfFileName = 'Incident Report'
      const pageWidth = 210 // A4 width in mm
      const centerPosition = Math.floor(pageWidth / 2)
      const margin = 10 // Margin on each side
      const pdf = new jsPDF('p', 'mm', 'a4')
      const headingArray = getHeadingInPdfExport()
      pdf.setFontSize(15)
      pdf.setFont('helvetica', 'bold') // Set the font to bold
      pdf.text('Incident Report', centerPosition, 10, {align: 'center'})
      let yPositionCurrent = 20

      headingArray?.forEach(({text, yPosition, fontSize}) => {
        yPositionCurrent += yPosition
        pdf.setFontSize(fontSize)
        pdf.setFont('helvetica', 'normal') // Reset font to normal
        pdf.text(text, 20, yPositionCurrent)
      })

      const barChartCanvasImage = await html2canvas(reportRef?.current, {scrollY: 0})
      const barChartImageData = barChartCanvasImage.toDataURL('image/png')

      const scaleFactor = 1 // Scale factor for enlarging the chart

      let imgWidth = Math.floor((pageWidth - margin) * scaleFactor) // Increase width
      let imgHeight = (barChartCanvasImage.height * imgWidth) / barChartCanvasImage.width // Maintain aspect ratio

      pdf.addImage(barChartImageData, 'PNG', margin, yPositionCurrent + 20, imgWidth, imgHeight)

      // Define the table headers and data
      const tableHeaders = [
        {header: 'Events', dataKey: 'events'},
        {header: 'Checkpoints', dataKey: 'checkpoints'},
        {header: 'No. of events', dataKey: 'noOfEvents'},
      ]
      // Transform the data to match autoTable format
      const transformedTableData = tableData.map((row) => ({
        events: row.events,
        checkpoints: row.checkpoints,
        noOfEvents: row.noOfEvents.toString(), // Ensure all values are strings if needed
      }))
      autoTable(pdf, {
        columns: tableHeaders,
        body: transformedTableData,
        startY: yPositionCurrent + imgHeight + 20,
        theme: 'grid', // Optional themes: 'striped', 'plain', 'grid'
        headStyles: {
          fillColor: [63, 81, 181], // Custom color for header background
          textColor: 255, // White text color
          fontSize: 12,
          fontStyle: 'bold',
          halign: 'center', // Center-align header text
        },
        bodyStyles: {
          halign: 'center', // Center-align body text
          fontSize: 10,
        },
        columnStyles: {
          shift: {cellWidth: 20}, // Adjust width of 'Shift' column
          // Set widths for other columns if needed
        },
      })
      pdf.save(pdfFileName)
    } catch (error) {
      console.error('scheduled report pdf file generate error', error)
      toast.error('Something went wrong, could not generate pdf')
    } finally {
      setExportToPdfLoading(false)
    }
  }

  useEffect(() => {
    if (!isIncidentTab) return
    fetchIncidentReports()
  }, [isIncidentTab])

  return (
    <Row>
      <Col md={3}>
        <AppSubmitButtonContainer className='mb-3'>
          <AppButton
            label='Get Incident Reports'
            onClick={handleGetIncidentReportClick}
            btnSize='sm'
            variant={'primary'}
            loading={reportFetchLoading}
            disabled={reportFetchLoading}
          />
          <AppButton
            label='Upload Incident Reports'
            onClick={handleExcelUploadBtnClick}
            btnSize='sm'
            variant={'success'}
            loading={reportUploadLoading}
            disabled={reportUploadLoading}
          />
        </AppSubmitButtonContainer>
        {incidentReports?.length > 0 ? (
          <AppListItems
            items={incidentReports}
            keyExtractor={({id}) => id}
            isSelectedItem={getIsIncidentReportSelected}
            labelExtractor={({label}) => label}
            getStyle={getIncidentReportStyle}
            onClickItem={handleIncidentReportClick}
          />
        ) : (
          <p className='h3 text-center'>No Reports found</p>
        )}
      </Col>
      <Col md={9}>
        {chartData.length === 0 || tableData.length === 0 ? (
          <div className='text-center h2'>No report selected</div>
        ) : (
          <div>
            {chartTableDataUploading ? (
              <AppLoaderWithContainer loading={chartTableDataUploading} />
            ) : (
              <>
                <div className='d-flex justify-content-end align-items-center my-3'>
                  <AppButton
                    label='Download Incident Report'
                    onClick={() => exportChartAndTableToPdf()}
                    btnSize='sm'
                    icon={'bi bi-download'}
                    loading={exportToPdfLoading}
                    disabled={exportToPdfLoading}
                  />
                </div>
                <div ref={reportRef}>
                  <Chart
                    width={'100%'}
                    height={'400px'}
                    chartType='BarChart'
                    loader={<div>Loading Chart...</div>}
                    data={incidentChartData}
                    options={{
                      title: 'Incident Report Data',
                      vAxis: {title: ''},
                    }}
                  />
                </div>
                <IncidentTableData
                  incidentReportcolumn={incidentReportcolumn}
                  tableData={tableData}
                />
              </>
            )}
          </div>
        )}
      </Col>
      <input type='file' ref={fileInputRef} className={`d-none`} onChange={getFile} />
    </Row>
  )
}

export default IncidentReport
